import { Button, Table } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Col, Container, Row } from 'reactstrap';

import { PathsLayouts } from '../../../../../config/routes';
import { moneyFormatter } from '../../../../../utils/currency';
import { useStudentCurrentAccountLoader } from '../../components/StudentCurrentAccountLoader';
import { listRefunds } from '../../Refunds/api';
import { Refund } from '../../Refunds/types';
import useColumns from '../hooks/useColumns';
import { Debt, DebtTableRow } from '../types';
import { useDebtLoader } from './DebtLoader';
import PaymentLinkModal, { PaymentLinkModalRef } from './PaymentLinkModal';

const keyPrefix = 'financialPortal.currentAccount.tabs.currentAccount';

// Paginación client-side: la lista de deudas+devoluciones de un estudiante es
// acotada, así que paginamos en memoria sobre las filas ya cargadas.
const PAGE_SIZE = 10;

interface DebtTableProps {
  /**
   * Cuando cambia, fuerza un refetch de devoluciones (HU-15411). El padre lo
   * incrementa después de crear/aplicar una devolución para reflejar el nuevo
   * estado en la misma tabla de Cuenta corriente sin recargar la vista.
   */
  refundsRefreshToken?: number;
}

function DebtTable({ refundsRefreshToken }: DebtTableProps) {
  const { t } = useTranslation();
  const history = useHistory();

  const { data: studentData } = useStudentCurrentAccountLoader();
  const studentId = studentData!.studentId;

  const [selectedDebts, setSelectedDebts] = useState<Debt[]>([]);
  const [refunds, setRefunds] = useState<Refund[]>([]);
  const [refundsLoading, setRefundsLoading] = useState<boolean>(false);
  // Página actual (1-based, como espera el paginador de @octano/global-ui).
  const [currentPage, setCurrentPage] = useState(1);

  const { data, loading } = useDebtLoader();
  const allowExternalDebtPaymentLink = data?.allowExternalDebtPaymentLink ?? false;
  const allowMultiDebtPaymentLink = data?.allowMultiDebtPaymentLink ?? false;
  // Los checkboxes ya impiden armar selecciones imposibles; esto es el cinturón
  // de seguridad para el estado en que quedó una selección cuando el dataset se
  // refrescó debajo.
  const selectedPayableDebts = useMemo(
    () => selectedDebts.filter((d) => d.payableExternalDebt),
    [selectedDebts],
  );
  const canGeneratePaymentLink =
    selectedPayableDebts.length > 0 &&
    selectedPayableDebts.length === selectedDebts.length &&
    (selectedDebts.length === 1 || allowMultiDebtPaymentLink);
  const selectedTotal = useMemo(
    () =>
      selectedDebts.reduce((sum, d) => sum + Number(d.outstandingDebt ?? 0), 0),
    [selectedDebts],
  );
  const paymentLinkModalRef = useRef<PaymentLinkModalRef>(null);

  useEffect(() => {
    let mounted = true;
    setRefundsLoading(true);
    listRefunds(studentId).then(({ data: list }) => {
      if (!mounted) {
        return;
      }
      setRefunds(list ?? []);
      setRefundsLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [studentId, refundsRefreshToken]);

  const onClickDetail = useCallback(
    (row: Debt) => {
      history.push(
        `${PathsLayouts.currentAccount}/${studentId}/debt-detail/${row.id}`,
      );
    },
    [history, studentId],
  );

  const onClickApplyRefund = useCallback(
    (refundId: string) => {
      history.push(
        `${PathsLayouts.currentAccount}/${studentId}/refunds/${refundId}/apply`,
      );
    },
    [history, studentId],
  );

  const onClickNotifySelectedDebts = useCallback((row: Debt) => {
    setSelectedDebts((prev) => {
      const exists = prev.some((d) => d.id === row.id);
      return exists ? prev.filter((d) => d.id !== row.id) : [...prev, row];
    });
  }, []);

  const columns = useColumns({
    onClickDetail,
    onClickApplyRefund,
    selectedDebts,
    onClickNotifySelectedDebts,
    allowExternalDebtPaymentLink,
    allowMultiDebtPaymentLink,
  });

  const rows = useMemo<DebtTableRow[]>(() => {
    const debtRows: DebtTableRow[] = (data?.debts ?? []).map((d) => ({
      __kind: 'debt' as const,
      ...d,
    }));
    const pendingRefunds = refunds.filter((r) => Number(r.amount) > 0);
    const refundRows: DebtTableRow[] = pendingRefunds.map((r) => ({
      __kind: 'refund' as const,
      id: r.id,
      refund: r,
    }));
    return [...debtRows, ...refundRows];
  }, [data?.debts, refunds]);

  // Al cambiar el dataset (refetch de deudas o devoluciones) volvemos a la
  // primera página para no quedar en una página vacía fuera de rango.
  useEffect(() => {
    setCurrentPage(1);
  }, [data?.debts, refunds]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const paginatedRows = useMemo<DebtTableRow[]>(
    () => rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [rows, currentPage],
  );

  return (
    <>
      <div className="divider my-4" />
      <Table
        columns={columns}
        data={paginatedRows}
        isLoadingResults={loading || refundsLoading}
        pagination={{
          totalItems: rows.length,
          itemsPerPage: PAGE_SIZE,
          totalPages,
          currentPage,
          onChangePage: setCurrentPage,
        }}
        noResultsText={
          <div className="text-center py-4">
            <p className="fs-18 fw-600 mb-1">
              {t(`${keyPrefix}.emptyTable.title`)}
            </p>
            <p className="text-muted mb-0">
              {t(`${keyPrefix}.emptyTable.body`)}
            </p>
          </div>
        }
      />
      <Container fluid className="mt-5">
        <Row>
          <Col xs={12} md={{ size: 6, offset: 6 }}>
            <Row>
              {allowExternalDebtPaymentLink && selectedDebts.length > 0 && (
                <Col xs={12} className="text-md-right mb-2">
                  <span className="fs-14 text-muted">
                    {t(`${keyPrefix}.selectionSummary`, {
                      count: selectedDebts.length,
                      total: moneyFormatter().format(Math.round(selectedTotal)),
                    })}
                  </span>
                </Col>
              )}
              <Col xs={12} md={allowExternalDebtPaymentLink ? 6 : 12}>
                <Button
                  type="button"
                  text={t(`${keyPrefix}.rescheduleDebts`)}
                  loading={loading}
                  size="md"
                  fullwidth
                  outlined
                  onClick={async () => {}}
                />
              </Col>
              {allowExternalDebtPaymentLink && (
                <Col xs={12} md={6}>
                  <Button
                    type="button"
                    onClick={() =>
                      canGeneratePaymentLink &&
                      paymentLinkModalRef.current?.openModal(
                        selectedPayableDebts,
                      )
                    }
                    text={t(`${keyPrefix}.generatePaymentLink`)}
                    size="md"
                    disabled={loading || !canGeneratePaymentLink}
                    fullwidth
                  />
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </Container>
      <PaymentLinkModal ref={paymentLinkModalRef} studentId={studentId} />
    </>
  );
}

export default DebtTable;
